import React from 'react';
import { Route } from 'react-router-dom';
import Providers from './Providers';
import { LazyLoad } from '@fortress-technology-solutions/fortress-component-library/HOC';
import { Box } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { AsyncBox } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import WelcomePage from 'pages/AffordaWelcomePage';
import AppBarPortal from 'components/Organisms/Appbar';
import useAffordaPortal from './hooks';
import Hero from 'components/Organisms/Hero';
import ScrollToTop from '../../components/Molecules/ScrollToTop';
import Dashboard from 'pages/AffordaDashboard';
import PATHS from 'constants/paths';

const Application = LazyLoad(() => import('pages/AffordaApplication'));
const Verification = LazyLoad(() => import('pages/AffordaVerification'));
const Documentation = LazyLoad(() => import('pages/AffordaDocumentation'));
const DocumentationAndUploads = LazyLoad(
  () => import('pages/AffordaDocumentsAndUploads'),
);
const ApplicationSubmission = LazyLoad(
  () => import('pages/AffordaApplicationSubmission'),
);
const BeforeYouGo = LazyLoad(() => import('pages/AffordaBeforeYouGo'));
const WagesAndIncome = LazyLoad(() => import('pages/AffordaWagesAndIncome'));
const AffordaAssets = LazyLoad(() => import('pages/AffordaAssets'));
const Household = LazyLoad(() => import('pages/AffordaHousehold'));
const Payments = LazyLoad(() => import('pages/AffordaPayments'));

const IdentifyingDetails = LazyLoad(
  () => import('pages/AffordaIdentifyingDetails'),
);
const CreditCriminalCheck = LazyLoad(
  () => import('pages/AffordaCreditAndCriminalCheck'),
);

const TermsAndConditions = LazyLoad(
  () => import('pages/AffordaTermsAndConditions'),
);

const AffordaPortal = ({ url }) => {
  const { showGradient, scrollRef, isLoading } = useAffordaPortal({ url });

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        height: '100vh',
        position: 'fixed',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        component={'div'}
        sx={{
          height: '100dvh',
          position: 'fixed',
          width: '100%',
          overflow: 'hidden',
          background: ({ palette }) =>
            showGradient
              ? `linear-gradient(180deg, #e4e4e4 0%, transparent 25%)`
              : `linear-gradient(180deg, ${palette.background.paper} 0%, transparent 25%)`,
          transition: ({ transitions }) => transitions.create('background'),
        }}
      >
        <AppBarPortal scrollRef={scrollRef} />
        <AsyncBox
          id={'scrollContainer'}
          loading={isLoading}
          sx={{
            height: '100dvh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            flexDirection: 'column',
            overflowY: 'auto',
            paddingX: 4,
            paddingBottom: 8,
            paddingTop: 12,
          }}
          ChildrenContainerProps={{
            sx: {
              minHeight: '50vh',
              maxWidth: { xs: 'calc(100vw - 32px)', sm: 550 },
              width: '100%',
              marginX: 'auto',
            },
          }}
        >
          <Route path={`${url}/${PATHS.WELCOME}`} exact component={Hero} />
          <Route path={`${url}/${PATHS.DASHBOARD}`} component={Hero} />
          <Route
            path={`${url}/${PATHS.WELCOME}`}
            render={() => <WelcomePage url={url} />}
          />
          <Route
            exact
            path={`${url}/${PATHS.DASHBOARD}`}
            component={Dashboard}
          />
          <Route
            exact
            path={`${url}/${PATHS.APPLICATION}`}
            component={Application}
          />
          <Route path={`${url}/${PATHS.HOUSEHOLD}`} component={Household} />

          <Route
            exact
            path={`${url}/${PATHS.VERIFICATION}`}
            component={Verification}
          />
          <Route path={`${url}/${PATHS.PAYMENTS}`} component={Payments} />

          <Route
            path={`${url}/${PATHS.WAGES_AND_INCOME}`}
            component={WagesAndIncome}
          />
          <Route path={`${url}/${PATHS.ASSETS}`} component={AffordaAssets} />
          <Route
            path={`${url}/${PATHS.IDENTIFYING_DETAILS}`}
            component={IdentifyingDetails}
          />
          <Route
            path={`${url}/${PATHS.CREDIT_CRIMINAL_CHECK}`}
            component={CreditCriminalCheck}
          />

          <Route
            exact
            path={`${url}/${PATHS.DOCUMENTATION}`}
            component={Documentation}
          />

          <Route
            path={`${url}/${PATHS.DOCUMENTS_AND_UPLOADS}`}
            component={DocumentationAndUploads}
          />

          <Route
            path={`${url}/${PATHS.APPLICATION_SUBMISSION}`}
            component={ApplicationSubmission}
          />

          <Route
            path={`${url}/${PATHS.BEFORE_YOU_GO}`}
            component={BeforeYouGo}
          />

          <Route
            exact
            path={`${url}/terms-and-conditions`}
            component={TermsAndConditions}
          />
        </AsyncBox>
        <ScrollToTop scrollRef={scrollRef} />
      </Box>
    </Box>
  );
};

const AffordaPortalWrapper = ({ url }) => {
  return (
    <Providers>
      <AffordaPortal url={url} />
    </Providers>
  );
};

export default AffordaPortalWrapper;
